import { IconType } from 'react-icons';
import { BiBookReader } from 'react-icons/bi';
import { BsClipboardCheck } from 'react-icons/bs';
import { FcReadingEbook } from 'react-icons/fc';
import { GrDocumentUser } from 'react-icons/gr';
import { LuLogOut } from 'react-icons/lu';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/context/useAuth';

import {
  Box,
  BoxProps,
  CloseButton,
  Flex,
  Text,
  VStack,
  HStack,
  Select,
  useDisclosure,
  Image,
} from '@chakra-ui/react';

import SidebarItem from './SidebarItem';
import { useTestCount } from '@/hooks/useTestCount';
import LogoutConfirmationDialog from '@/components/Modals/LogoutConfirmationDialog';
import { useTranslation } from 'react-i18next';
import { openInNewTab } from '@/utils/openInNewTab';
import { useTrimmedFullName } from '@/utils/useTrimmedFullName';
import { FaHome } from 'react-icons/fa';

interface LinkItemProps {
  name: string;
  icon: IconType;
  to: string;
  onClick?: () => void;
}

interface SidebarProps extends BoxProps {
  onClose: () => void;
  isInDrawer?: boolean;
}

const Sidebar = ({ onClose, isInDrawer = false, ...rest }: SidebarProps) => {
  const { totalTestCount } = useTestCount();
  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguage();
  const { logout, fullName } = useAuth();
  const trimmedFullName = useTrimmedFullName(fullName);
  const {
    isOpen: isLogoutDialogOpen,
    onOpen: onLogoutDialogOpen,
    onClose: onLogoutDialogClose,
  } = useDisclosure();

  const LinkItems: LinkItemProps[] = [
    {
      name: t('top'),
      icon: FaHome,
      to: '/top',
    },
    {
      name: t('digital-learning-materials'),
      icon: BiBookReader,
      to: '/home',
    },
    {
      name: t('test'),
      icon: BsClipboardCheck,
      to: '/test',
    },
    {
      name: t('learning-records'),
      icon: GrDocumentUser,
      to: '/profile',
    },
    {
      name: t('logout-button'),
      icon: LuLogOut,
      to: '#',
      onClick: onLogoutDialogOpen,
    },
  ];

  const handleLanguageChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const lang = event.target.value;
    changeLanguage.mutate(lang);
  };

  return (
    <Box
      bg="white"
      borderRight={isInDrawer ? 'none' : '1px'}
      borderRightColor={isInDrawer ? 'transparent' : 'gray.200'}
      w={isInDrawer ? '100%' : { base: 'full', md: '260px' }}
      pos={isInDrawer ? 'static' : 'fixed'}
      h={isInDrawer ? '100%' : '100%'}
      overflowY="auto"
      sx={{
        WebkitOverflowScrolling: 'touch',
        ...(isInDrawer
          ? {}
          : {
              transform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
              transition: 'all 0.2s ease',
            }),
        // Respect motion preferences
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
      }}
      {...rest}
    >
      {/* Mobile Header*/}
      <Flex
        h={{ base: '16', md: '20' }}
        alignItems="center"
        mx="4"
        justifyContent="flex-end"
        borderBottom={{ base: '1px', md: 'none' }}
        borderColor="gray.100"
        display={{ base: 'flex', md: 'none' }}
      >
        <Image
          src="/ビギナーズロゴ_20250513.png"
          alt="logo"
          h="60px"
          objectFit="contain"
          alignSelf="center"
          w="100%"
        />
        <CloseButton
          onClick={onClose}
          size="lg"
          color="gray.600"
          _hover={{ bg: 'gray.100' }}
          sx={{
            transition: 'background-color 0.15s ease',
          }}
        />
      </Flex>

      {/* Mobile user info */}
      <Box display={{ base: 'block', md: 'none' }} mb={4} mx={4} mt={2}>
        <VStack align="start" spacing={3}>
          <HStack spacing={3}>
            <FcReadingEbook size={36} />
            <Text fontSize="md" fontWeight="semibold" color="gray.700">
              {`${t('welcome')} ${trimmedFullName} さん`}
            </Text>
          </HStack>
        </VStack>
      </Box>
      <Flex
        direction="column"
        h={{ base: 'calc(100% - 130px)', md: '100%' }}
        justify="space-between"
      >
        <Box>
          <Image
            src="/ビギナーズロゴ_20250513.png"
            alt="logo"
            h="65px"
            objectFit="contain"
            alignSelf="center"
            w="100%"
            mt={{ base: 4, md: 4 }}
            mb={{ base: 2, md: 4 }}
            display={{ base: 'none', md: 'block' }}
          />
          <Select
            onChange={handleLanguageChange}
            value={language}
            maxW={{ base: 'calc(100% - 32px)', md: '207px' }}
            fontSize={{ base: '14px', lg: '16px' }}
            borderColor="#fa9710"
            color="gray.800"
            fontWeight="600"
            borderRadius="8px"
            mx={4}
            mb={{ base: 4, md: 3 }}
            bg="white"
          >
            <option value="en">English</option>
            <option value="ja">日本語</option>
            <option value="vi">Tiếng Việt</option>
            <option value="zh-pinyin">中文(简体)</option>
            <option value="id">Bahasa Indonesia</option>
            <option value="pt-BR">Português (Brasil)</option>
            <option value="ne">नेपाली</option>
            <option value="hi">हिन्दी</option>
          </Select>

          {LinkItems.map((link) => (
            <SidebarItem
              key={link.name}
              icon={link.icon}
              to={link.to}
              onClick={link.onClick}
              notificationCount={
                link.name === t('test') ? totalTestCount : undefined
              }
            >
              {link.name}
            </SidebarItem>
          ))}
        </Box>
        <Box p={{ base: 3, md: 4 }} borderTop="1px" borderColor="gray.100">
          <VStack spacing={3} justifyContent="center" align="start">
            <Text
              fontSize={{ base: 'xs', md: 'sm' }}
              color="gray.600"
              _hover={{
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => openInNewTab('/privacy-policy')}
            >
              {t('privacy-policy')}
            </Text>
            <Text
              fontSize={{ base: 'xs', md: 'sm' }}
              color="gray.600"
              _hover={{
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => openInNewTab('/terms')}
            >
              {t('terms-of-usage')}
            </Text>
            <Text
              fontSize={{ base: 'xs', md: 'sm' }}
              color="gray.600"
              _hover={{
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => openInNewTab('/law')}
            >
              {t('law')}
            </Text>
          </VStack>
        </Box>
      </Flex>

      <LogoutConfirmationDialog
        isOpen={isLogoutDialogOpen}
        onClose={onLogoutDialogClose}
        onConfirm={() => {
          onLogoutDialogClose();
          logout();
        }}
      />
    </Box>
  );
};

export default Sidebar;
